/**
 * Tipos del dominio económico.
 *
 * Todo importe es un entero de centavos (D4). Los kilogramos usan 3 decimales,
 * igual que `numeric(12,3)` en el modelo de datos.
 */
import type { Cents, Kg } from '@/core/money/cents';

export type ParticipantId = string;

/** Una línea de reparto. `sortOrder` define el desempate del mayor resto (D4). */
export interface SplitLine {
  participantId: ParticipantId;
  /** Porcentaje 0–100, hasta 4 decimales, como `numeric(7,4)`. */
  percentage: number;
  sortOrder: number;
}

export type Calculation = 'per_unit' | 'per_job';

export type MobilityMode = 'none' | 'per_km_fuel' | 'per_km_fixed' | 'flat' | 'manual';

export interface WorkItemInput {
  serviceName: string;
  calculation: Calculation;
  /** Kg de novillo por unidad (o por trabajo si `calculation` es `per_job`). */
  kgPerUnit: Kg;
  quantity: number;
}

export interface MobilityInput {
  mode: MobilityMode;
  km?: Kg | null;
  /**
   * Importe por kilómetro en centavos.
   * En `per_km_fuel` es el precio de referencia del combustible; en `per_km_fixed`,
   * la tarifa fija por km configurada por la organización.
   */
  ratePerKmCents?: Cents | null;
  /** Importe total en centavos. Solo para `flat` y `manual`. */
  amountCents?: Cents | null;
  /** Beneficiarios de la movilidad. Debe sumar 100 % si hay importe. */
  allocations: readonly SplitLine[];
}

export interface WorkSessionInput {
  id: string;
  billingYear: number;
  billingMonth: number;
  billerParticipantId: ParticipantId;
  /** Precio del novillo por kg, en centavos. `null` = precio pendiente (Spec §14). */
  steerPricePerKgCents: Cents | null;
  items: readonly WorkItemInput[];
  profitSplits: readonly SplitLine[];
  mobility?: MobilityInput | null;
}

export interface WorkAmounts {
  totalKg: Kg;

  /**
   * Importes en centavos, o `null` cuando falta un precio de referencia.
   *
   * `null` significa **PENDIENTE**, no cero. Un importe pendiente no puede
   * sumarse, liquidarse, exportarse ni cerrarse: las funciones del motor lo
   * rechazan explícitamente en lugar de tratarlo como 0.
   *
   * El cero sí es un importe válido y real: por ejemplo, movilidad con
   * `mode = 'none'` (Caso F) o un trabajo sin kg que valorizar.
   */
  honorariaCents: Cents | null;
  mobilityCents: Cents | null;
  totalCents: Cents | null;

  /** `true` si algún importe quedó pendiente por falta de precio de referencia. */
  pricePending: boolean;
}

/** Un asiento de ajuste. Sus líneas deben sumar exactamente 0 (D3, invariante I8). */
export interface AdjustmentBatch {
  id: string;
  concept: string;
  entries: readonly AdjustmentEntry[];
}

export interface AdjustmentEntry {
  participantId: ParticipantId;
  amountCents: Cents;
}

export interface SettlementEntry {
  participantId: ParticipantId;
  earnedHonorariaCents: Cents;
  earnedMobilityCents: Cents;
  adjustmentsCents: Cents;
  billedCents: Cents;
  /** earned + adjustments − billed. Positivo: le deben. Negativo: debe. */
  balanceCents: Cents;
}

export interface Transfer {
  fromParticipantId: ParticipantId;
  toParticipantId: ParticipantId;
  amountCents: Cents;
}

export class EconomicsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EconomicsError';
  }
}

/**
 * Se lanza al intentar operar económicamente sobre trabajos cuyo importe
 * todavía no puede determinarse por falta de precio de referencia.
 * Lleva los ids para que la UI pueda listarlos (regla de cierre).
 */
export class PendingPriceError extends EconomicsError {
  readonly pendingWorkIds: readonly string[];

  constructor(pendingWorkIds: readonly string[]) {
    super(
      `Hay ${pendingWorkIds.length} trabajo(s) sin precio de referencia: ` +
        `${pendingWorkIds.join(', ')}. No es posible liquidar ni cerrar el período.`,
    );
    this.name = 'PendingPriceError';
    this.pendingWorkIds = pendingWorkIds;
  }
}
